import { RefObject, useCallback, useEffect, useRef, useState } from 'react';

export function useResize<T extends HTMLElement = HTMLDivElement>({
  minSize,
  isHeight,
}: {
  minSize: number;
  isHeight?: boolean;
}): {
  ref: RefObject<T>;
  size: number;
  isResizing: boolean;
  enableResize: (event: MouseEvent | React.MouseEvent) => void;
} {
  const [isResizing, setIsResizing] = useState(false);
  const [size, setSize] = useState(minSize);
  const ref = useRef<T>(null);
  const startClientXRef = useRef<number>(0);
  const startClientYRef = useRef<number>(0);
  const startSizeRef = useRef<number>(minSize);

  const enableResize = useCallback(
    (event: MouseEvent | React.MouseEvent) => {
      if ('preventDefault' in event) event.preventDefault();
      setIsResizing(true);
      startClientXRef.current = event.clientX ?? 0;
      startClientYRef.current = event.clientY ?? 0;
      if (ref.current) {
        const rect = ref.current.getBoundingClientRect();
        startSizeRef.current = isHeight ? rect.height : rect.width;
      } else {
        startSizeRef.current = size;
      }
    },
    [isHeight, size],
  );

  const disableResize = useCallback(() => {
    setIsResizing(false);
  }, []);

  const resize = useCallback(
    (event: MouseEvent | React.MouseEvent) => {
      if (!isResizing) return;
      const deltaX = event.clientX - startClientXRef.current;
      const deltaY = event.clientY - startClientYRef.current;
      if (isHeight) {
        const newHeight = Math.max(minSize, startSizeRef.current + deltaY);
        setSize(newHeight);
      } else {
        const newWidth = Math.max(minSize, startSizeRef.current + deltaX);
        setSize(newWidth);
      }
    },
    [isHeight, isResizing, minSize],
  );

  useEffect(() => {
    document.addEventListener('mousemove', resize);
    document.addEventListener('mouseup', disableResize);

    return () => {
      document.removeEventListener('mousemove', resize);
      document.removeEventListener('mouseup', disableResize);
    };
  }, [disableResize, resize]);

  return { ref, size, isResizing, enableResize };
}
